import { Container, Graphics, filters } from "pixi.js"
import { CompositeTilemap } from "@pixi/tilemap"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { Entity, Coordinate } from "game-common/models"
import { Fader } from "./gui/fader"
import { CoordinatePool } from "game-common/coordinate_pool"
import { setFineToRoughCoordinates, fillCoordinate, isEqual, blankCoordinate } from "game-common/util"
import { logger } from "../../../logger"

/**
 * Configuration options for the SkyManager
 */
export interface SkyManagerConfig {
    skySurface: Container
    tilemapMetaProvider: TileMapMetaProvider
}

/**
 * Interface for sky management functionality
 */
export interface ISkyManager {
    // Sky visibility control
    setStaticHidingAllowed(allowed: boolean): void
    updatePlayerLocation(entity: any, force?: boolean): void

    // Sky rendering
    clearSkySurfaces(): void
    getSkyTilemapRenderer(): CompositeTilemap
    getSkySpriteSurfaceAbove(): Container
    getSkySpriteSurfaceBelow(): Container

    // Sky state
    isUnderSky(): boolean | undefined
    isSkyHidingAllowed(): boolean

    // Configuration
    updateTilemapMetaProvider(tilemapMetaProvider: TileMapMetaProvider): void
    resetSkyState(): void
}

/**
 * Manages sky-related rendering and visibility
 * Handles sky tile rendering, visibility transitions, and fading effects
 */
export class SkyManager implements ISkyManager {
    private skySurface: Container
    private skyTilemapRenderer: CompositeTilemap
    private skySpriteSurfaceAbove: Container
    private skySpriteSurfaceBelow: Container
    // `filters` is a const object in pixi's typings, not a namespace, so it cannot be
    // used in a type position directly.
    private skyAlphFilter: InstanceType<typeof filters.AlphaFilter>
    private skyHidingAllowed: boolean = true
    private currentUnderSky: boolean | undefined = undefined
    private currentSkyFader: Fader | undefined = undefined
    private tilemapMetaProvider: TileMapMetaProvider | undefined
    private currentRoughCoordinates: Coordinate = blankCoordinate()

    constructor(config: SkyManagerConfig) {
        this.skySurface = config.skySurface
        this.tilemapMetaProvider = config.tilemapMetaProvider

        // Initialize sky components
        this.skyTilemapRenderer = new CompositeTilemap()
        this.skySpriteSurfaceAbove = new Graphics()
        this.skySpriteSurfaceBelow = new Graphics()
        this.skyAlphFilter = new filters.AlphaFilter(1.0)

        // Setup sky surface. The AlphaFilter is attached ONLY while a fade is in progress (see
        // updatePlayerLocation) -- a permanently attached filter forces the whole sky surface to
        // render-to-texture every frame even at full alpha, a constant cost on integrated GPUs.
        this.skySurface.addChild(this.skySpriteSurfaceBelow)
        this.skySurface.addChild(this.skyTilemapRenderer)
        this.skySurface.addChild(this.skySpriteSurfaceAbove)
    }

    // ============================================================================
    // Sky Visibility Control
    // ============================================================================

    /**
     * Set whether sky hiding is allowed
     */
    public setStaticHidingAllowed = (allowed: boolean): void => {
        this.skyHidingAllowed = allowed
    }

    /**
     * Update sky visibility based on player location
     */
    public updatePlayerLocation = (entity: Entity, force?: boolean): void => {
        if (!this.tilemapMetaProvider || !entity) {
            return
        }

        // turn sky map layer on or off based on player position
        const item = CoordinatePool.instance.checkout()
        setFineToRoughCoordinates(entity.location, item.data)
        if (force || !isEqual(item.data, this.currentRoughCoordinates)) {
            fillCoordinate(item.data, this.currentRoughCoordinates)
            const isSkyTile = this.tilemapMetaProvider.isSkyTileAt(item.data.x, item.data.y)

            if (isSkyTile !== this.currentUnderSky) {
                if (isSkyTile && this.skyHidingAllowed) {
                    if (this.currentSkyFader) {
                        this.currentSkyFader.stop()
                    }

                    // Attach the filter for the duration of the fade-out only.
                    this.skySurface.filters = [this.skyAlphFilter]
                    this.currentSkyFader = new Fader(this.skyAlphFilter, {
                        // fadeInterval: 15,
                        direction: "out",

                        releaseCallback: () => {
                            this.skySurface.visible = false
                            this.skyAlphFilter.alpha = 0.0
                            // Hidden surface needs no filter.
                            this.skySurface.filters = null
                        },
                    })
                    this.currentSkyFader.update()
                } else if (!this.skySurface.visible) {
                    if (this.currentSkyFader) {
                        this.currentSkyFader.stop()
                    }

                    this.skySurface.visible = true
                    // Attach the filter only for the fade-in; drop it once fully opaque so the
                    // steady-state visible sky renders without a per-frame filter pass.
                    this.skySurface.filters = [this.skyAlphFilter]
                    this.skyAlphFilter.alpha = 0.0
                    this.currentSkyFader = new Fader(this.skyAlphFilter, {
                        fadeInterval: 1,
                        direction: "in",
                        minAlpha: 1.0,
                        releaseCallback: () => {
                            this.skyAlphFilter.alpha = 1.0
                            this.skySurface.filters = null
                        },
                    })
                    this.currentSkyFader.update()
                }

                this.currentUnderSky = isSkyTile
            }
        }
        CoordinatePool.instance.release(item)
    }

    // ============================================================================
    // Sky Rendering
    // ============================================================================

    /**
     * Clear all sky surfaces
     */
    public clearSkySurfaces = (): void => {
        this.skyTilemapRenderer.clear()
        this.skySpriteSurfaceAbove.removeChildren()
        this.skySpriteSurfaceBelow.removeChildren()
    }

    /**
     * Get the sky tilemap renderer
     */
    public getSkyTilemapRenderer = (): CompositeTilemap => {
        return this.skyTilemapRenderer
    }

    /**
     * Get the sky sprite surface above
     */
    public getSkySpriteSurfaceAbove = (): Container => {
        return this.skySpriteSurfaceAbove
    }

    /**
     * Get the sky sprite surface below
     */
    public getSkySpriteSurfaceBelow = (): Container => {
        return this.skySpriteSurfaceBelow
    }

    // ============================================================================
    // Sky State
    // ============================================================================

    /**
     * Check if currently under sky
     */
    public isUnderSky = (): boolean | undefined => {
        return this.currentUnderSky
    }

    /**
     * Check if sky hiding is allowed
     */
    public isSkyHidingAllowed = (): boolean => {
        return this.skyHidingAllowed
    }

    // ============================================================================
    // Configuration
    // ============================================================================

    /**
     * Update the tilemap meta provider
     */
    public updateTilemapMetaProvider = (tilemapMetaProvider: TileMapMetaProvider): void => {
        this.tilemapMetaProvider = tilemapMetaProvider
    }

    /**
     * Reset sky state (called when changing rooms)
     */
    public resetSkyState = (): void => {
        this.skyHidingAllowed = true
        this.currentUnderSky = undefined
        if (this.currentSkyFader) {
            this.currentSkyFader.stop()
            this.currentSkyFader = undefined
        }
    }
}
